import {
  datadogRum,
  DefaultPrivacyLevel,
  type RumErrorEvent,
} from '@datadog/browser-rum';
interface WindowWithDatadog extends Window {
  getDatadogSessionId?: () => string | undefined;
}

export const dataDogPlugin = (_, options) => {
  // https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/#filter-out-bot-sessions-on-intake
  const botTraffic =
    '(googlebot\\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|HeadlessChrome|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis|HeadlessChrome|CAVU Automation \\(exclude-from-analytics\\))';

  const regex = new RegExp(botTraffic, 'i');

  const userAgent = navigator.userAgent;

  const conditionalSampleRate = regex.test(userAgent) ? 0 : 100;

  const applicationId = process.env['NUXT_ENV_DATADOG_RUM_APPLICATION_ID'];
  const clientToken = process.env['NUXT_ENV_DATADOG_RUM_CLIENT_TOKEN'];

  if (applicationId === undefined) {
    throw new Error(
      'NUXT_ENV_DATADOG_RUM_APPLICATION_ID environment variable is required for data dog'
    );
  }

  if (clientToken === undefined) {
    throw new Error(
      'NUXT_ENV_DATADOG_RUM_CLIENT_TOKEN environment variable is required for data dog'
    );
  }

  const defaultPrivacyLevel = process.env[
    'NUXT_ENV_DATADOG_RUM_DEFAULT_PRIVACY_LEVEL'
  ] as DefaultPrivacyLevel | undefined;

  const env = process.env['NUXT_ENV_DATADOG_RUM_ENVIRONMENT'];
  const service = options.serviceName;

  const configuredSampleRate = process.env['NUXT_ENV_DATADOG_RUM_SESSION_SAMPLE_RATE']
    ? Number(process.env['NUXT_ENV_DATADOG_RUM_SESSION_SAMPLE_RATE'])
    : 100;

  // Use conditionalSampleRate (0 for bots, 100 for regular users)
  // If not a bot, use the configured sample rate from env var
  const sessionSampleRate = conditionalSampleRate === 0 ? 0 : configuredSampleRate;

  const site = process.env['NUXT_ENV_DATADOG_RUM_SITE'];

  const trackLongTasks = process.env['NUXT_ENV_DATADOG_RUM_TRACK_LONG_TASKS']
    ? Boolean(process.env['NUXT_ENV_DATADOG_RUM_TRACK_LONG_TASKS'])
    : undefined;

  const trackResources = process.env['NUXT_ENV_DATADOG_RUM_TRACK_RESOURCES']
    ? Boolean(process.env['NUXT_ENV_DATADOG_RUM_TRACK_RESOURCES'])
    : undefined;

  const trackUserInteractions = process.env[
    'NUXT_ENV_DATADOG_RUM_TRACK_USER_INTERACTIONS'
  ]
    ? Boolean(process.env['NUXT_ENV_DATADOG_RUM_TRACK_USER_INTERACTIONS'])
    : undefined;

  const trackSessionAcrossSubdomains = true;
  const allowedTracingUrls = [
    (url: string) => {
      return (
        !url.includes('appspot.com') &&
        !url.includes('feefo.com') &&
        (url.includes('cavu-tech.com') || url.includes('cavu-services.com'))
      );
    },
  ];

  const enableExperimentalFeatures = ['feature_flags'];
  const sessionReplaySampleRate = 0;

  // Assign a version for deployment tracking in datadog (required for source maps)
  // internal domains will default to the current commit hash
  // external domains should use the release version (v1.xx.x)
  let versionId = process.env.NUXT_ENV_COMMIT_HASH
  if (window.version !== 'latest') {
    versionId = window.version
  }
  let environmentName = process.env.NUXT_ENV_ENVIRONMENT
  if (environmentName === 'development') {
    environmentName = 'dev';
  }
  const version = `${environmentName}-${versionId}`

  // ERRORS: in the event we need to add more errors to ignore
  // https://github.com/DataDog/browser-sdk/issues/2715
  // https://docs.bird.com/pusher/channels/channels/troubleshooting/what-is-meant-by-channels-error-1006
  const errorPatternsToIgnore = [
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
    /PusherError/,                                                       // Pusher connection errors (code 1006) tab closing
    /bot/i,                                                              // Bot detection errors
    /Script error/i,                                                     // 3rd party script errors
    /Empty reason/i,                                                     // 3rd party script errors with no message
  ];

  datadogRum.init({
    applicationId,
    clientToken,
    defaultPrivacyLevel,
    env,
    service,
    sessionSampleRate,
    sessionReplaySampleRate,
    site,
    trackLongTasks,
    trackResources,
    trackUserInteractions,
    trackSessionAcrossSubdomains,
    allowedTracingUrls,
    enableExperimentalFeatures,
    beforeSend: (event) => {
      if (event.error) {
        const errorMessage = (event as RumErrorEvent).error.message;
        if (errorPatternsToIgnore.some(pattern => pattern.test(errorMessage))) {
          return false;
        }
      }
      return true;
    },
    version
  });

  if (typeof window !== 'undefined') {
    (window as WindowWithDatadog).getDatadogSessionId = () => {
      return datadogRum.getInternalContext()?.session_id;
    };
  }
};

export default dataDogPlugin;
